import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom"; // useParams 가져오기
import { useCallback } from "react";
import Slider from "react-slick"; // React Slick
import { collection, getDocs } from "firebase/firestore";
import { db } from '../../firebase'; // Firestore 초기화 가져오기
import './css/MusicArchive.scss';
import Loading from "../Loading";


const MusicArchive = () => {
  const [activeTab, setActiveTab] = useState("ownAlbums"); // 초기 탭 설정
  const [albums, setAlbums] = useState([]);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [prevButton, setPrevButton] = useState("hidden");
  const [nextButton, setNextButton] = useState("visible");
  const [tabVisibility, setTabVisibility] = useState({
    ownAlbums: false,
    collaboAlbums: false,
    unreleasedAlbums: false,
  });

  const { id: musicianId } = useParams(); // URL에서 musicianId 가져오기

  // Firestore에서 앨범과 트랙 데이터 가져오기
  const fetchAlbums = useCallback(
    async (albumType) => {
      try {
        const albumsRef = collection(db, "musicians", musicianId, albumType);
        const snapshot = await getDocs(albumsRef);
    
        if (snapshot.empty) {
          console.error(`No albums found in ${albumType}`);
          return [];
        }
    
        const albums = snapshot.docs.map((albumDoc) => ({
          id: albumDoc.id,
          ...albumDoc.data(),
          year: new Date(albumDoc.data().year), // 문자열을 Date 객체로 변환
          tracks: [], // 트랙 데이터를 초기에는 비워둠
        }));
    
        // 최신순 정렬
        albums.sort((a, b) => b.year - a.year);
    
        return albums;
      } catch (error) {
        return [];
      }
    },
    [musicianId]
  );

  const fetchTracksForAlbum = useCallback(async (albumType, albumId) => {
    const tracksRef = collection(
      db,
      "musicians",
      musicianId,
      albumType,
      albumId,
      "tracks"
    );
    const tracksSnapshot = await getDocs(tracksRef);
  
    return tracksSnapshot.docs.map((trackDoc) => ({
      id: trackDoc.id,
      ...trackDoc.data(),
    }));
  }, [musicianId]); // ✅ `musicianId`가 변경될 때만 새로운 함수 생성    

  const handleAlbumClick = async (album) => {
    if (!album.tracks || album.tracks.length === 0) {  // 트랙이 없는 경우만 로드
      const tracks = await fetchTracksForAlbum(activeTab, album.id);
      setSelectedAlbum({ ...album, tracks: [...tracks] }); // ✅ 새로운 객체 할당 (리렌더링 강제)
    } else {
      setSelectedAlbum({ ...album }); // ✅ 새로운 객체로 상태 변경 (React가 감지하도록)
    }
  };    
  

  // 탭 데이터 유무 확인 및 상태 업데이트
  useEffect(() => {
    const checkTabs = async () => {
      setLoading(true);
  
      // 1️⃣ Firestore에서 앨범 목록 가져오기
      const ownAlbums = await fetchAlbums("ownAlbums");
      const collaboAlbums = await fetchAlbums("collaboAlbums");
      const unreleasedAlbums = await fetchAlbums("unreleasedAlbums");
  
      setTabVisibility({
        ownAlbums: ownAlbums.length > 0,
        collaboAlbums: collaboAlbums.length > 0,
        unreleasedAlbums: unreleasedAlbums.length > 0,
      });
  
      // 2️⃣ 최신 앨범을 찾기
      let initialTab = "";
      let initialAlbums = [];
  
      if (ownAlbums.length > 0) {
        initialTab = "ownAlbums";
        initialAlbums = ownAlbums;
      } else if (collaboAlbums.length > 0) {
        initialTab = "collaboAlbums";
        initialAlbums = collaboAlbums;
      } else if (unreleasedAlbums.length > 0) {
        initialTab = "unreleasedAlbums";
        initialAlbums = unreleasedAlbums;
      }
  
      setActiveTab(initialTab);
      setAlbums(initialAlbums);
  
      // 3️⃣ 최신 앨범이 있을 경우, 트랙을 즉시 불러와서 `setSelectedAlbum` 설정
      if (initialAlbums.length > 0) {
        const firstAlbum = initialAlbums[0]; // 최신 앨범 가져오기
        const tracks = await fetchTracksForAlbum(initialTab, firstAlbum.id); // 트랙 로드
        setSelectedAlbum({ ...firstAlbum, tracks }); // 최신 앨범 & 트랙 설정
      }
  
      setLoading(false);
    };
  
    checkTabs();
  }, [fetchAlbums, fetchTracksForAlbum]);

  // 🚀 탭 전환 시 앨범 데이터 가져오기
  useEffect(() => {
    setLoading(true);

    const fetchData = async () => {
      const albumData = await fetchAlbums(activeTab);
      setAlbums(albumData);

      if (albumData.length > 0) {
        setSelectedAlbum(albumData[0]); // 🎯 트랙 없이 UI 먼저 표시
      }

      setLoading(false);
    };

    fetchData();
  }, [activeTab, fetchAlbums]);

  // 🚀 선택된 앨범의 트랙을 비동기적으로 로드 (안전한 의존성 배열 사용)
  useEffect(() => {
    const fetchTracksForSelectedAlbum = async () => {
      if (selectedAlbum && selectedAlbum.id) {
        const tracks = await fetchTracksForAlbum(activeTab, selectedAlbum.id);
        setSelectedAlbum(prevAlbum => ({ ...prevAlbum, tracks: [...tracks] })); // ✅ 새로운 객체로 상태 업데이트
      }
    };

    fetchTracksForSelectedAlbum();
  }, [activeTab, fetchTracksForAlbum, selectedAlbum]); // ✅ 안전한 의존성 배열

  // 슬라이더 설정
  const CustomPrevArrow = ({ className, style, onClick }) => (
    <button className={`archiveArrow prev ${ prevButton }`} onClick={onClick}>
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24">
        <path d="M13.293 6.293 7.586 12l5.707 5.707 1.414-1.414L10.414 12l4.293-4.293z"></path>
      </svg>
    </button>
  );
  const CustomNextArrow = ({ className, style, onClick }) => (
    <button className={`archiveArrow next ${ nextButton }`} onClick={ onClick }>
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24">
        <path d="M10.707 17.707 16.414 12l-5.707-5.707-1.414 1.414L13.586 12l-4.293 4.293z"></path>
      </svg>
    </button>
  );

  const sliderSettings = {
    dots: true,
    // infinite: albums.length > 3,
    infinite: false,
    speed: 500,
    slidesToShow: Math.min(albums.length, 3),
    slidesToScroll: 1,
    centerMode: albums.length < 3,
    centerPadding: albums.length === 2 ? "50px" : "0",
    prevArrow: <CustomPrevArrow />,
    nextArrow: <CustomNextArrow />,
    // responsive: [
    //   {
    //     breakpoint: 768,
    //     settings: {
    //       slidesToShow: Math.min(albums.length, 1),
    //       centerMode: false,
    //     },
    //   },
    // ],
    afterChange: (index) => {
      if (index === 0) {
        setPrevButton('hidden');
      } else {
        setPrevButton('visible');
      }
      if (index + 3 > albums.length - 1) {
        setNextButton('hidden');
      } else {
        setNextButton('visible');
      }
    }
  };

  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="music-archive">
      {/* 로딩 상태 처리 */}
      {
        loading
        ?
        <Loading />
        :
        albums.length === 0
        ?
        (<p className='noData'>No data available yet. :(</p>)
        :
        (
          <>
            {/* 탭 네비게이션 */}
            <div className="tab-navigation">
              {tabVisibility.ownAlbums && (
                <button
                  className={`tab ${activeTab === "ownAlbums" ? "active" : ""}`}
                  onClick={() => setActiveTab("ownAlbums")}
                >
                  Musician Albums
                </button>
              )}
              {tabVisibility.collaboAlbums && (
                <button
                  className={`tab ${activeTab === "collaboAlbums" ? "active" : ""}`}
                  onClick={() => setActiveTab("collaboAlbums")}
                >
                  Collaboration Albums
                </button>
              )}
              {tabVisibility.unreleasedAlbums && (
                <button
                  className={`tab ${activeTab === "unreleasedAlbums" ? "active" : ""}`}
                  onClick={() => setActiveTab("unreleasedAlbums")}
                >
                  Unreleased
                </button>
              )}
            </div>
    
            {/* 앨범 슬라이더 */}
            {albums.length > 0 ? (
              <Slider {...sliderSettings}>
                {albums.map((album) => (
                  <div
                    key={album.id}
                    className="album-card"
                    onClick={() => handleAlbumClick(album)}
                  >
                    <div className="album-wrap">
                      <img
                        src={album.cover}
                        alt={album.title}
                        className="album-cover"
                      />
                      <div className="album-info">
                        <h3 className="album-title">{album.title}</h3>
                        <p className="album-year">{album.year.toLocaleDateString()}</p>
                        {album.artist && (
                          <p className="album-year">{album.artist}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </Slider>
            ) : (
              <p className="no-albums">No albums available.</p>
            )}
    
            {/* 앨범 설명 및 트랙리스트 */}
            {selectedAlbum && (
              <div className="album-details">
                <h3 className="album-description-title">{selectedAlbum.title}</h3>
                {selectedAlbum.description && (
                  <div className="album-description">
                    {isExpanded ? (
                      selectedAlbum.description // ✅ '더보기' 버튼을 눌렀을 때 전체 내용 표시
                    ) : (
                      `${selectedAlbum.description.slice(0, 200)}` // ✅ 200자까지만 표시
                    )}
                    {selectedAlbum.description.length > 200 && (
                      <button className="expand-button" onClick={() => setIsExpanded(!isExpanded)}>
                        {isExpanded ? "접기" : "... 더보기"}
                      </button>
                    )}
                  </div>
                )}
                {selectedAlbum.tracks.length > 0 ? (
                  <ul className="track-list">
                    {[...selectedAlbum.tracks]
                      .sort((a, b) => {
                        const aId = a.id ? parseInt(a.id, 10) : Infinity;
                        const bId = b.id ? parseInt(b.id, 10) : Infinity;
                        return aId - bId;
                      })
                      .map((track, index) => (
                        <li key={track.id || index} className="track">
                          <span className="track-number">{index + 1} </span>
                          <a
                            href={track.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="track-title"
                          >
                            {track.title.replace("[TITLE]", "").trim()}
                            {track.title.includes("[TITLE]") && (
                              <span className="title-highlight">[TITLE]</span>
                            )}
                          </a>
                          <div className="track-roles">
                            {track.roles
                              ?.split(",")
                              .map((role, idx) => (
                                <span key={idx} className="role-tag">
                                  {role.trim()}
                                </span>
                              ))}
                          </div>
                        </li>
                      ))}
                  </ul>
                ) : (
                  <p className="no-tracks">No tracks available for this album.</p>
                )}
              </div>
            )}
          </>
          )
      }
    </div>
  );
  
};

export default MusicArchive;
